import { View } from '@play-co/timestep-core/ui';
import { Vector2, type System, randomItem } from '../../odie';
import { GameScene } from '../GameScene';
import { CubeEffect } from '../actions/effects/CubeEffect';
import { BlockId } from '../defs/block';
import { blockIterateAll } from '../util/blockTools';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { BlockGenAlgorithm } from '../defs/map';

/**
 * Handles match2 gameplay cheats, on dev builds only
 */
export class CheatSystem implements System {
  public static readonly NAME = 'cheat';
  public scene!: GameScene;
  private ladder!: LangBitmapFontTextView;
  private obstacleCompletion!: LangBitmapFontTextView;
  private moveCompletion!: LangBitmapFontTextView;
  private startingAlgorithm!: LangBitmapFontTextView;
  private dynamicAlgorithm!: LangBitmapFontTextView;
  private log!: View;
  private blockGenAlgorithm?: BlockGenAlgorithm;

  // Init Rubberbanding debug UI
  public init() {
    const width = 600;
    const height = 100;
    this.log = new View({
      superview: this.scene.stage.view,
      width,
      height,
      backgroundColor: 'rgba(0,0,0,0.9)',
      x: -width * 0.5,
      y: -425,
      blockEvents: true,
      visible: false,
    });

    this.ladder = new LangBitmapFontTextView({
      superview: this.log,
      font: 'Body',
      color: '#fff',
      size: 24,
      localeText: () => 'Step: X',
      centerOnOrigin: true,
      x: 15,
      y: 5,
    });

    this.obstacleCompletion = new LangBitmapFontTextView({
      superview: this.log,
      font: 'Body',
      color: '#fff',
      size: 24,
      localeText: () => 'Obs Compl: X',
      centerOnOrigin: true,
      x: 170,
      y: 5,
    });

    this.moveCompletion = new LangBitmapFontTextView({
      superview: this.log,
      font: 'Body',
      color: '#fff',
      size: 24,
      localeText: () => 'Move Depl: X',
      centerOnOrigin: true,
      x: 390,
      y: 5,
    });

    this.startingAlgorithm = new LangBitmapFontTextView({
      superview: this.log,
      font: 'Body',
      color: '#fff',
      size: 24,
      localeText: () => 'Starting: X',
      centerOnOrigin: true,
      x: 15,
      y: 35,
    });

    this.dynamicAlgorithm = new LangBitmapFontTextView({
      superview: this.log,
      font: 'Body',
      color: '#fff',
      size: 24,
      localeText: () => 'Dynamic: X',
      centerOnOrigin: true,
      x: 15,
      y: 65,
    });
  }

  public setStep(step: number) {
    this.ladder.localeText = () => `Step: ${step}`;
  }

  public setObstacleCompletion(percent: number) {
    this.obstacleCompletion.localeText = () => `Obs Compl: ${percent}%`;
  }

  public setMoveCompletion(percent: number) {
    this.moveCompletion.localeText = () => `Move Compl: ${percent}%`;
  }

  public setStartAlgorithm(algorithm: string) {
    this.startingAlgorithm.localeText = () => `Start: ${algorithm}`;
  }

  public setDynamicAlgorithm(algorithm: string) {
    this.dynamicAlgorithm.localeText = () => `Dynamic: ${algorithm}`;
  }

  public async bigLolipopExplosion(times = 1) {
    for (let i = 0; i < times; i++) {
      await new CubeEffect(this.scene, {
        position: new Vector2(4, 4),
        color: 'all',
      }).execute();
    }
  }

  public placeRocketVertical() {
    this.placeBlock('rocketVertical');
  }

  public placeRocketHorizontal() {
    this.placeBlock('rocketHorizontal');
  }

  public placeBomb() {
    this.placeBlock('bomb');
  }

  public placeCube() {
    const cubeColors: string[] = [];

    blockIterateAll(this.scene, (block) => {
      if (block.c.block.blockId.startsWith('basic')) {
        cubeColors.push(block.c.block.blockId.replace('basic', 'cube'));
      }
    });

    this.placeBlock(randomItem(cubeColors) as BlockId);
  }

  public placeBlock(id: BlockId) {
    this.scene.phaseSystem['_inputPhase'].interceptTap = (cell) => {
      const block = cell.base?.entity;
      if (block) {
        this.scene.basicBlockSystem.turnBlockInto(block, id);
      }
    };
  }

  public toggleLog(visible?: boolean) {
    this.log.updateOpts({
      visible: visible !== undefined ? visible : !this.log.style.visible,
    });
  }

  public setBlockGenAlgorithm(algorithm: BlockGenAlgorithm) {
    this.blockGenAlgorithm = algorithm;
    this.setDynamicAlgorithm(algorithm);
  }

  public getBlockGenAlgorithm() {
    return this.blockGenAlgorithm;
  }

  public async loseGame() {
    this.scene.moves = 0;
    await this.scene.updateRound();
  }

  public async winGame() {
    const goals = this.scene.sessionEntity.c.map.goals;
    Object.keys(goals).forEach((goal) => {
      goals[goal as keyof typeof goals] = 0;
    });
    await this.scene.updateRound();
  }

  public async setMoves(moves: number) {
    this.scene.moves = moves + 1; // +1 to trigger move event
    this.scene.events.publish({ id: 'move' });
  }
}
